import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function compactText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function brandMatchesQuery(row, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const name = String(row?.brand || "");
  const brandId = String(row?.BrandID || "");
  const qCompact = compactText(q);
  const nameLower = name.toLowerCase();
  const idLower = brandId.toLowerCase();
  const nameCompact = compactText(name);
  const idCompact = compactText(brandId);
  return (
    nameLower.includes(q) ||
    idLower.includes(q) ||
    (qCompact && (nameCompact.includes(qCompact) || idCompact.includes(qCompact)))
  );
}

function BrandSearchSelect({
  brands = [],
  value,
  onChange,
  placeholder,
  emptyText,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);

  const selected = useMemo(
    () => brands.find((row) => String(row._id) === String(value)) || null,
    [brands, value]
  );

  const filtered = useMemo(
    () => (open ? brands.filter((row) => brandMatchesQuery(row, query)) : brands),
    [brands, open, query]
  );

  useEffect(() => {
    if (open) return;
    setQuery("");
  }, [open, value]);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    function placeMenu() {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuStyle({
        position: "fixed",
        left: r.left,
        top: r.bottom + 4,
        width: r.width,
        zIndex: 80,
      });
    }
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, filtered.length]);

  function pick(row) {
    onChange(String(row?._id || ""));
    setQuery("");
    setOpen(false);
  }

  const inputValue = open ? query : selected?.brand || "";

  return (
    <div className="relative mt-1">
      <input
        ref={inputRef}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => {
          if (disabled) return;
          setQuery("");
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            return;
          }
          if (e.key !== "Enter" || !open) return;
          e.preventDefault();
          if (filtered[0]) pick(filtered[0]);
        }}
      />
      {open && menuStyle
        ? createPortal(
            <div
              className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
              style={menuStyle}
            >
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-500">{emptyText}</p>
              ) : (
                filtered.map((row) => {
                  const id = String(row._id);
                  const active = id === String(value);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                        active ? "bg-[#f8f1e4]" : ""
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(row)}
                    >
                      <span className="font-medium text-slate-800">{row.brand}</span>
                      {row.BrandID ? (
                        <span className="shrink-0 font-mono text-xs text-slate-400">{row.BrandID}</span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default BrandSearchSelect;
